import "./TaskListView.css";

import { Fragment, useEffect, useState } from "react";

import { ChevronRightIcon, MoreVerticalDarkIcon } from "../../../../../assets/icons";
import { DeadlineColourClass, getDeadlineColourClass } from "../../../../../utils";
import { TaskTags } from "../../../../data/TaskTags";
import { Button } from "../../../../forms/Button";
import { DropdownButton, DropdownItem } from "../../../../forms/DropdownButton";
import { getTaskType } from "../../../../TaskCard/utils";
import { COMPLETED_COLUMN_ID, KANBAN_COLUMN_LABELS, OBJECT_TYPE_LABELS } from "../../constants";
import {
  KanbanColumnId,
  KanbanTask,
  TasksDashboardListGroupRow,
  TasksDashboardListSectionRow,
  TasksDashboardVariant,
} from "../../models";
import { resolveTaskColumn } from "../../utils";

interface TaskListViewProps {
  /**
   * The server's group rows and their status sections — one group per parent object. Every count is
   * the server's, and each section's rows load on expand and page independently, so the table is
   * never limited to the tasks that happen to be loaded elsewhere on the dashboard.
   */
  groups: TasksDashboardListGroupRow[];
  variant?: TasksDashboardVariant;
  isLoading?: boolean;
  onTaskClick?: (taskId: string) => void;
  /** Fired by the "Table options → Export table" action. */
  onExportTable?: () => void;
  /** Disables the "Export table" item while an export is running — it is a multi-page fetch. */
  isExporting?: boolean;
  /** Fired for every section of an expanded group — load its first page of rows. */
  onSectionExpand?: (objectUuid: string, status: string) => void;
  /** Fired by a section's "Show more" — load its next page of rows. */
  onSectionShowMore?: (objectUuid: string, status: string) => void;
  /** True when the server holds further groups beyond `groups` — drives "Show more objects". */
  hasMoreGroups?: boolean;
  isLoadingMoreGroups?: boolean;
  onShowMoreGroups?: () => void;
}

/** Status pill tint per column — reuses the TaskCard status-tag palette, extended for the two
 *  columns TaskCard doesn't model (in-review, unassigned). */
const STATUS_CLASS: Record<KanbanColumnId, string> = {
  unassigned: "TaskListView_status_unassigned",
  "not-started": "TaskListView_status_notStarted",
  "in-progress": "TaskListView_status_inProgress",
  "in-review": "TaskListView_status_inReview",
  blocked: "TaskListView_status_blocked",
  completed: "TaskListView_status_completed",
};

/** Parse the board's date strings — ISO or dd/mm/yyyy (UK, day-first). */
const parseDate = (value?: string): Date | undefined => {
  if (!value) return undefined;
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const dmy = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
  return undefined;
};

/**
 * Deadline-proximity dot. The bands are the app-wide ones (`getDeadlineColourClass`) — the same
 * timings the project tasks board and DateWithIndicator use — rather than thresholds local to this
 * table: overdue / ≤1wk / ≤1mo / beyond.
 */
const DOT_CLASS: Record<DeadlineColourClass, string> = {
  VeryHigh: "TaskListView_dot_overdue",
  High: "TaskListView_dot_urgent",
  Medium: "TaskListView_dot_warning",
  Low: "TaskListView_dot_onTrack",
};

const COLUMN_COUNT = 5;

/**
 * A placeholder row shaped like a real one: one block per column, each sized like the content it
 * stands in for. A single block spanning the row read as "the first two columns are loading and the
 * rest are empty", which is not what is happening.
 */
const TaskListViewSkeletonRow = (): JSX.Element => (
  <tr className="TaskListView_row" aria-hidden>
    <td className="TaskListView_cell TaskListView_cell_task">
      <span className="TaskListView_skeleton TaskListView_skeleton_task" />
    </td>
    <td className="TaskListView_cell">
      <span className="TaskListView_skeleton TaskListView_skeleton_tags" />
    </td>
    <td className="TaskListView_cell">
      <span className="TaskListView_skeleton TaskListView_skeleton_status" />
    </td>
    <td className="TaskListView_cell">
      <span className="TaskListView_skeleton TaskListView_skeleton_deadline" />
    </td>
    <td className="TaskListView_cell TaskListView_cell_assignee">
      <span className="TaskListView_skeleton TaskListView_skeleton_assignee" />
    </td>
  </tr>
);

export const TaskListView = ({
  groups,
  variant = "seed",
  isLoading = false,
  onTaskClick,
  onExportTable,
  isExporting = false,
  onSectionExpand,
  onSectionShowMore,
  hasMoreGroups = false,
  isLoadingMoreGroups = false,
  onShowMoreGroups,
}: TaskListViewProps): JSX.Element => {
  /*
    Groups start collapsed: a section's rows are only fetched on expand, so opening every group would
    fire a request per status section of every object on the page.
  */
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());

  const toggle = (objectUuid: string): void =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(objectUuid)) {
        next.delete(objectUuid);
      } else {
        next.add(objectUuid);
      }
      return next;
    });

  /*
    Tell the host which sections are open so it can load their rows. Deliberately not deduplicated
    here: the host owns the row cache and clears it when its query changes, so a set remembered
    across those reloads would leave a still-open section permanently empty. Re-announcing a cached
    section is harmless — the host's own cache drops it — and a new `onSectionExpand` identity
    re-announces everything, which is what reloads the rows under a new query.
  */
  useEffect(() => {
    if (!onSectionExpand) return;

    groups.forEach(({ objectUuid, sections }) => {
      if (!openGroups.has(objectUuid)) return;
      sections.forEach(({ status }) => onSectionExpand(objectUuid, status));
    });
  }, [onSectionExpand, groups, openGroups]);

  /*
    The same chips the Kanban card renders, from the same widget. The list used to own a parallel
    set of `TaskListView_tag_*` styles, which drifted from the card's — the same tag showed in two
    colours depending on which view you were in.

    The parent object chip is deliberately not passed: the group header the row sits under already
    names the object, so repeating it in every row says nothing the reader doesn't have.
  */
  const renderTags = (task: KanbanTask): JSX.Element => (
    <TaskTags
      isMandatory={task.isMandatory}
      isMilestone={task.isMilestone}
      taskType={getTaskType(task.hasWorkflow, task.isGeneral)}
      extraTags={task.tags ?? []}
    />
  );

  const renderTaskRow = (task: KanbanTask): JSX.Element => {
    const column = resolveTaskColumn(task);
    const deadline = parseDate(task.dueDate);
    /*
      A task completed after its deadline is not overdue any more (REQ-009), so the dot on a
      Completed row is never a proximity warning. A task with no deadline gets no dot at all — a
      green "on track" marker against a blank date would be asserting something we don't know.
    */
    const dotClass = column === COMPLETED_COLUMN_ID ? DOT_CLASS.Low : DOT_CLASS[getDeadlineColourClass(deadline)];

    return (
      <tr key={task.id} className="TaskListView_row">
        <td className="TaskListView_cell TaskListView_cell_task">
          {onTaskClick ? (
            <button type="button" className="TaskListView_taskName" onClick={() => onTaskClick(task.id)}>
              {task.title}
            </button>
          ) : (
            <span className="TaskListView_taskName">{task.title}</span>
          )}
        </td>
        <td className="TaskListView_cell">{renderTags(task)}</td>
        <td className="TaskListView_cell">
          <span className={`TaskListView_status ${STATUS_CLASS[column]}`}>{KANBAN_COLUMN_LABELS[column]}</span>
        </td>
        <td className="TaskListView_cell">
          <span className="TaskListView_deadline">
            {deadline && <span className={`TaskListView_dot ${dotClass}`} aria-hidden />}
            {task.dueDate}
          </span>
        </td>
        <td className="TaskListView_cell TaskListView_cell_assignee">
          {task.assigneeInitials && (
            <span className="TaskListView_avatar" title={task.assigneeName} aria-label={task.assigneeName}>
              {task.assigneeInitials}
            </span>
          )}
        </td>
      </tr>
    );
  };

  /*
    A status section of an open group. `count` is the server's total for `(object, status)`, so the
    "Show more" below the rows is driven by `hasMoreTasks` rather than by comparing rows to the count.
  */
  const renderSection = (objectUuid: string, section: TasksDashboardListSectionRow): JSX.Element => {
    const { status, count, isLoadingTasks = false, hasMoreTasks = false } = section;
    const sectionTasks = section.tasks ?? [];

    return (
      <Fragment key={`${objectUuid}|${status}`}>
        <tr className="TaskListView_sectionRow">
          <td className="TaskListView_sectionCell" colSpan={COLUMN_COUNT}>
            <span className="TaskListView_sectionName">{KANBAN_COLUMN_LABELS[section.columnId]}</span>
            <span className="TaskListView_groupCount">{count}</span>
          </td>
        </tr>

        {sectionTasks.map(renderTaskRow)}

        {isLoadingTasks && sectionTasks.length === 0 && <TaskListViewSkeletonRow />}

        {hasMoreTasks && (
          <tr className="TaskListView_row">
            <td className="TaskListView_cell TaskListView_cell_showMore" colSpan={COLUMN_COUNT}>
              <Button
                text={isLoadingTasks ? "Loading…" : `Show more (${count - sectionTasks.length} remaining)`}
                variant="secondary"
                size="extraSmall"
                minWidth={false}
                disabled={isLoadingTasks}
                onClick={() => onSectionShowMore?.(objectUuid, status)}
              />
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  return (
    <div className={`TaskListView TaskListView_${variant}`}>
      <div className="TaskListView_toolbar">
        <h3>All tasks</h3>
        <DropdownButton
          className="TaskListView_options"
          ariaLabel="Table options"
          component={
            <Button
              variant="secondary"
              // Same size as the table's "Show more" buttons — `small` carries a 44px min-height,
              // which reads as an oversized control against the section headings.
              size="extraSmall"
              minWidth={false}
              icon={<MoreVerticalDarkIcon aria-hidden />}
              iconPosition="right"
              text="Table options"
            />
          }
        >
          {/* Export re-queries the whole filtered set rather than serialising the sections that
              happen to be expanded, so it is a multi-page fetch and blocks a second run. */}
          <DropdownItem
            className={isExporting ? "TaskListView_optionsItem_disabled" : ""}
            onClick={isExporting ? undefined : () => onExportTable?.()}
          >
            {isExporting ? "Exporting…" : "Export table"}
          </DropdownItem>
        </DropdownButton>
      </div>

      <div className="TaskListView_tableWrap">
        <table className="TaskListView_table">
          <thead>
            <tr className="TaskListView_headerRow">
              <th className="TaskListView_th TaskListView_th_task">Task</th>
              <th className="TaskListView_th">Tags</th>
              <th className="TaskListView_th">Status</th>
              <th className="TaskListView_th">Deadline</th>
              <th className="TaskListView_th TaskListView_th_assignee">Assignee</th>
            </tr>
          </thead>

          {isLoading ? (
            <tbody>
              {[0, 1, 2, 3].map((i) => (
                <TaskListViewSkeletonRow key={i} />
              ))}
            </tbody>
          ) : (
            groups.map((group) => {
              const open = openGroups.has(group.objectUuid);
              return (
                <tbody key={group.objectUuid} className="TaskListView_group">
                  <tr className="TaskListView_groupRow">
                    <td className="TaskListView_groupCell" colSpan={COLUMN_COUNT}>
                      <button
                        type="button"
                        className="TaskListView_groupToggle"
                        aria-expanded={open}
                        onClick={() => toggle(group.objectUuid)}
                      >
                        <ChevronRightIcon
                          aria-hidden
                          className={`TaskListView_chevron${open ? " TaskListView_chevron_open" : ""}`}
                        />
                        <span className="TaskListView_groupName">{group.objectName}</span>
                        {/* The parent's type, inline — the group set spans projects, deals and
                            landholdings, so the name alone doesn't say which this is. */}
                        {group.objectType && (
                          <span className="TaskListView_groupType">
                            {OBJECT_TYPE_LABELS[group.objectType.toLowerCase()] ?? group.objectType}
                          </span>
                        )}
                        <span className="TaskListView_groupCount">{group.taskCount}</span>
                      </button>
                    </td>
                  </tr>
                  {open && group.sections.map((section) => renderSection(group.objectUuid, section))}
                </tbody>
              );
            })
          )}
        </table>

        {/* The groups are paged server-side, so without this the objects after the first page
            would never be reachable from the List. */}
        {!isLoading && hasMoreGroups && (
          <div className="TaskListView_groupsShowMore">
            <Button
              text={isLoadingMoreGroups ? "Loading…" : "Show more objects"}
              variant="secondary"
              size="small"
              minWidth={false}
              disabled={isLoadingMoreGroups}
              onClick={() => onShowMoreGroups?.()}
            />
          </div>
        )}

        {!isLoading && groups.length === 0 && <p className="TaskListView_empty">No tasks to show.</p>}
      </div>
    </div>
  );
};
